import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../lib/auth';
import { scr } from '../../lib/api';
import type { DistrictCaseload, IntegritySavingsStats, OverdueAppeals, Referral, WelfareCaseload } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { ErrorNote, Field, PageHeader, Spinner, StatCard } from '../../ui/components';

const REGISTRY_ROLES = ['ASP_REGISTRY_OFFICER', 'DISTRICT_WELFARE_OFFICER', 'ASP_ADMIN'];
const WELFARE_ROLES = ['DISTRICT_WELFARE_OFFICER', 'ASP_SENIOR_OFFICER', 'ASP_ADMIN'];
const INTEGRITY_ROLES = ['ASP_INTEGRITY_OFFICER', 'ASP_CEO', 'OAG_AUDITOR'];
const APPEALS_ROLES = ['APPEALS_SECRETARY', 'DISTRICT_MANAGER', 'ASP_ADMIN'];
const BOARD_ROLES = ['BOARD_SECRETARY', 'BOARD_MEMBER'];

export function Dashboard() {
  const { persona } = useAuth();
  const roles = persona?.roles ?? [];
  const [districtId, setDistrictId] = useState(persona?.districtId ?? 'beau-vallon');

  const canRegistry = roles.some((r) => REGISTRY_ROLES.includes(r));
  const canWelfare = roles.some((r) => WELFARE_ROLES.includes(r));
  const canIntegrity = roles.some((r) => INTEGRITY_ROLES.includes(r));
  const canAppeals = roles.some((r) => APPEALS_ROLES.includes(r));
  const canBoard = roles.some((r) => BOARD_ROLES.includes(r));

  const registryQ = useApiQuery<DistrictCaseload>(canRegistry ? `/api/v1/registry/districts/${districtId}/caseload` : null);
  const welfareQ = useApiQuery<WelfareCaseload>(canWelfare ? `/api/v1/welfare/caseload/${districtId}` : null);
  const savingsQ = useApiQuery<IntegritySavingsStats>(canIntegrity ? '/api/v1/integrity/stats/savings' : null);
  const overdueQ = useApiQuery<OverdueAppeals>(canAppeals ? '/api/v1/cases/appeals/overdue' : null);
  const queueQ = useApiQuery<{ items: Referral[] }>(canBoard ? '/api/v1/medboard/queue' : null);

  return (
    <div>
      <PageHeader title="Dashboard" subtitle={`Signed in as ${persona?.name} — ${persona?.title}`} />

      {(canRegistry || canWelfare) ? (
        <div className="mb-4 max-w-xs">
          <Field label="District">
            <input className="field-input" value={districtId} onChange={(e) => setDistrictId(e.target.value)} placeholder="e.g. beau-vallon" />
          </Field>
        </div>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {canRegistry ? (
          registryQ.loading ? (
            <Spinner />
          ) : registryQ.error ? (
            <div className="col-span-2">
              <ErrorNote message={registryQ.error} />
            </div>
          ) : (
            <>
              <StatCard label="Registered beneficiaries" value={registryQ.data?.beneficiaries ?? 0} hint={`District: ${districtId.replaceAll('-', ' ')}`} />
              <StatCard label="Households" value={registryQ.data?.households ?? 0} />
            </>
          )
        ) : null}

        {canWelfare ? (
          welfareQ.loading ? (
            <Spinner />
          ) : welfareQ.error ? (
            <div className="col-span-2">
              <ErrorNote message={welfareQ.error} />
            </div>
          ) : (
            <>
              <StatCard label="Open welfare applications" value={welfareQ.data?.openApplications ?? 0} />
              <StatCard label="Active welfare awards" value={welfareQ.data?.activeAwards ?? 0} hint={scr(welfareQ.data?.monthlyCommitment ?? 0) + '/mo committed'} />
            </>
          )
        ) : null}

        {canIntegrity ? (
          savingsQ.loading ? (
            <Spinner />
          ) : savingsQ.error ? (
            <div className="col-span-2">
              <ErrorNote message={savingsQ.error} />
            </div>
          ) : (
            <>
              <StatCard label="Confirmed integrity cases" value={savingsQ.data?.confirmedCases ?? 0} tone="warn" />
              <StatCard label="Recovered" value={scr(savingsQ.data?.recoveredSCR ?? 0)} tone="good" />
              <StatCard label="Prevented (annualised)" value={scr(savingsQ.data?.preventedAnnualisedSCR ?? 0)} tone="good" hint="the Minister's number" />
            </>
          )
        ) : null}

        {canAppeals ? (
          overdueQ.loading ? (
            <Spinner />
          ) : overdueQ.error ? (
            <div className="col-span-2">
              <ErrorNote message={overdueQ.error} />
            </div>
          ) : (
            <>
              <StatCard
                label="Appeals — ack. overdue"
                value={overdueQ.data?.acknowledgementOverdue.length ?? 0}
                tone={(overdueQ.data?.acknowledgementOverdue.length ?? 0) > 0 ? 'bad' : 'good'}
              />
              <StatCard
                label="Appeals — decision overdue"
                value={overdueQ.data?.decisionOverdue.length ?? 0}
                tone={(overdueQ.data?.decisionOverdue.length ?? 0) > 0 ? 'bad' : 'good'}
              />
            </>
          )
        ) : null}

        {canBoard ? (
          queueQ.loading ? (
            <Spinner />
          ) : queueQ.error ? (
            <div className="col-span-2">
              <ErrorNote message={queueQ.error} />
            </div>
          ) : (
            <StatCard label="Medical board queue (referred)" value={queueQ.data?.items.length ?? 0} />
          )
        ) : null}

        {!canRegistry && !canWelfare && !canIntegrity && !canAppeals && !canBoard ? (
          <p className="col-span-4 text-sm text-slate-500">
            Use the navigation on the left to get to your work — {persona?.title} doesn't have a summary card configured on this dashboard yet.
          </p>
        ) : null}
      </div>

      <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-4 text-sm">
        <Link to="/asp-staff/registry" className="card p-4 hover:border-brand-500">
          <div className="font-semibold text-slate-900">Registry</div>
          <div className="text-xs text-slate-500">Households, beneficiaries, vital flags</div>
        </Link>
        <Link to="/asp-staff/claims" className="card p-4 hover:border-brand-500">
          <div className="font-semibold text-slate-900">Claims</div>
          <div className="text-xs text-slate-500">13 statutory benefit types</div>
        </Link>
        <Link to="/asp-staff/welfare" className="card p-4 hover:border-brand-500">
          <div className="font-semibold text-slate-900">Welfare Assessment</div>
          <div className="text-xs text-slate-500">Means-tested applications</div>
        </Link>
        <Link to="/asp-staff/integrity" className="card p-4 hover:border-brand-500">
          <div className="font-semibold text-slate-900">Integrity</div>
          <div className="text-xs text-slate-500">Ghost-beneficiary matching</div>
        </Link>
      </div>
    </div>
  );
}
